import type { MetaContext } from "./types";
import { contentLanguage, localeUrl } from "./seo-url";
import { ORGANIZATION } from "@/lib/organization";

const SITE_NAME = "Psihotron";
const DEFAULT_OG_IMAGE = "/og-default.jpg";

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface PageStructuredDataOptions {
  titleKey: string;
  descriptionKey: string;
  ogImage?: string;
  pageType?: string | readonly string[];
  breadcrumbs?: readonly BreadcrumbItem[];
  mainEntity?: (
    ctx: MetaContext,
    ids: { organizationId: string; pageId: string },
  ) => Record<string, unknown> | undefined;
  extraGraph?: (
    ctx: MetaContext,
    ids: { organizationId: string; pageId: string },
  ) => Record<string, unknown>[];
}

function absUrl(maybeRelative: string, origin: string): string {
  if (/^https?:\/\//i.test(maybeRelative)) return maybeRelative;
  return new URL(maybeRelative, origin).toString();
}

export function buildPageStructuredData(
  ctx: MetaContext,
  opts: PageStructuredDataOptions,
): Record<string, unknown> {
  const origin = ctx.url.origin;
  const canonical = localeUrl(origin, ctx.url.pathname, ctx.locale);
  const organizationId = `${origin}/#organization`;
  const websiteId = `${origin}/#website`;
  const pageId = `${canonical}#webpage`;
  const imageUrl = absUrl(opts.ogImage ?? DEFAULT_OG_IMAGE, origin);
  const inLanguage = contentLanguage(ctx.locale);

  const graph: Record<string, unknown>[] = [
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": organizationId,
      name: ORGANIZATION.name,
      legalName: ORGANIZATION.legalName,
      url: origin,
      logo: `${origin}/logo.svg`,
      image: `${origin}/og-default.jpg`,
      description: ctx.t("org.description"),
      foundingDate: ORGANIZATION.foundingYear,
      identifier: ORGANIZATION.registrationNumber,
      taxID: ORGANIZATION.idNumber,
      vatID: ORGANIZATION.vatNumber,
      telephone: ORGANIZATION.phoneE164,
      email: ORGANIZATION.email,
      founder: {
        "@type": "Person",
        name: ORGANIZATION.director,
        jobTitle: ORGANIZATION.directorTitle,
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: ORGANIZATION.streetAddress,
        postalCode: ORGANIZATION.postalCode,
        addressLocality: ORGANIZATION.city,
        addressCountry: ORGANIZATION.countryCode,
      },
      knowsAbout: [
        "Kognitivno-bihevioralna terapija",
        "Geštalt psihoterapija",
        "Sistemska porodična psihoterapija",
        "EMDR",
        "Transakciona analiza",
        "Realitetna terapija",
      ],
      sameAs: [...ORGANIZATION.socials],
      areaServed: {
        "@type": "Country",
        name: "Bosnia and Herzegovina",
      },
      contactPoint: {
        "@type": "ContactPoint",
        email: ORGANIZATION.email,
        telephone: ORGANIZATION.phoneE164,
        contactType: "customer support",
        areaServed: "BA",
        availableLanguage: ["bs", "en"],
      },
    },
    {
      "@type": "WebSite",
      "@id": websiteId,
      url: origin,
      name: SITE_NAME,
      publisher: { "@id": organizationId },
      inLanguage: ["bs-BA", "en"],
    },
    {
      "@type": opts.pageType ?? "WebPage",
      "@id": pageId,
      url: canonical,
      name: ctx.t(opts.titleKey),
      description: ctx.t(opts.descriptionKey),
      isPartOf: { "@id": websiteId },
      about: { "@id": organizationId },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: imageUrl,
      },
      inLanguage,
    },
  ];

  const mainEntity = opts.mainEntity?.(ctx, { organizationId, pageId });
  if (mainEntity) {
    graph[2] = { ...graph[2], mainEntity: mainEntity["@id"] ? { "@id": mainEntity["@id"] } : mainEntity };
    graph.push(mainEntity);
  }

  if (opts.breadcrumbs?.length) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: opts.breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: localeUrl(origin, item.path, ctx.locale),
      })),
    });
  }

  const extra = opts.extraGraph?.(ctx, { organizationId, pageId }) ?? [];
  for (const node of extra) graph.push(node);

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
